import { CONFIG } from '../config.js';

const C = CONFIG.COLORS;

// Placeholder textures match Kenney scale (16x16) so a single 2.5x sprite scale
// works whether the real PNG loaded or we fell back to a generated circle.
function make16Character(scene, key, fillColor, opts = {}) {
  if (scene.textures.exists(key)) return;
  const size = 16;
  const cx = 8;
  const cy = 8;
  const radius = 6;
  const g = scene.add.graphics({ x: 0, y: 0, add: false });
  g.fillStyle(fillColor, 1);
  g.fillCircle(cx, cy, radius);
  g.lineStyle(1, 0x0f0d2c, 1);
  g.strokeCircle(cx, cy, radius);
  if (opts.eyes !== false) {
    g.fillStyle(0xffffff, 1);
    g.fillRect(cx - 3, cy - 1, 1, 1);
    g.fillRect(cx + 2, cy - 1, 1, 1);
  }
  if (opts.fangs) {
    g.fillStyle(0xffffff, 1);
    g.fillRect(cx - 2, cy + 2, 1, 1);
    g.fillRect(cx + 1, cy + 2, 1, 1);
  }
  g.generateTexture(key, size, size);
  g.destroy();
}

function makePickupTexture(scene, key, fillColor) {
  if (scene.textures.exists(key)) return;
  const r = 11;
  const size = r * 2 + 6;
  const c = r + 3;
  const g = scene.add.graphics({ x: 0, y: 0, add: false });
  g.fillStyle(fillColor, 0.25);
  g.fillCircle(c, c, r + 2);
  g.fillStyle(fillColor, 1);
  g.fillCircle(c, c, r);
  g.lineStyle(2, 0xffffff, 1);
  g.strokeCircle(c, c, r);
  g.generateTexture(key, size, size);
  g.destroy();
}

export function generatePlaceholderTextures(scene) {
  // Characters — only generated if the real PNG didn't load.
  make16Character(scene, 'hero', C.HERO);
  // Basic
  make16Character(scene, 'enemy-slime', C.SLIME);
  make16Character(scene, 'enemy-bat', C.BAT, { fangs: true });
  make16Character(scene, 'enemy-goblin', C.GOBLIN, { fangs: true });
  make16Character(scene, 'enemy-wolf', C.WOLF, { fangs: true });
  make16Character(scene, 'enemy-spider', C.SPIDER);
  // Advanced
  make16Character(scene, 'enemy-skeleton', C.GOBLIN, { fangs: true });
  make16Character(scene, 'enemy-darkKnight', C.WOLF, { fangs: true });
  make16Character(scene, 'enemy-cursedVillager', C.SPIDER);
  make16Character(scene, 'enemy-slimeVariant', C.SLIME);
  // Expert
  make16Character(scene, 'enemy-redMage', C.BOSS, { fangs: true });
  make16Character(scene, 'enemy-apprentice', C.SPIDER);
  make16Character(scene, 'enemy-royalSpider', C.WOLF, { fangs: true });
  make16Character(scene, 'enemy-fastSlime', C.SLIME);
  // Bosses
  make16Character(scene, 'boss', C.BOSS, { fangs: true });
  make16Character(scene, 'boss-mistress', C.BOSS, { fangs: true });
  make16Character(scene, 'boss-knight', C.WOLF, { fangs: true });
  make16Character(scene, 'boss-captain', C.GOBLIN, { fangs: true });

  // Bullets, particles, pickups, tile — always generated (no real PNGs for them).
  if (!scene.textures.exists('bullet')) {
    const g = scene.add.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(C.BULLET, 1);
    g.fillCircle(5, 5, 5);
    g.lineStyle(1, 0xffffff, 0.9);
    g.strokeCircle(5, 5, 4);
    g.generateTexture('bullet', 10, 10);
    g.destroy();
  }
  if (!scene.textures.exists('bullet-crit')) {
    const g = scene.add.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(C.BULLET_CRIT, 1);
    g.fillCircle(7, 7, 7);
    g.lineStyle(1, 0xffffff, 1);
    g.strokeCircle(7, 7, 6);
    g.generateTexture('bullet-crit', 14, 14);
    g.destroy();
  }

  makePickupTexture(scene, 'pickup-heart', C.HEART);
  makePickupTexture(scene, 'pickup-gem', C.GEM);
  makePickupTexture(scene, 'pickup-speed', C.SPEED);
  makePickupTexture(scene, 'pickup-shield', C.SHIELD);
  makePickupTexture(scene, 'pickup-doubleDmg', C.DOUBLE_DMG);

  if (!scene.textures.exists('particle')) {
    const g = scene.add.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(0xffffff, 1);
    g.fillCircle(3, 3, 3);
    g.generateTexture('particle', 6, 6);
    g.destroy();
  }
  if (!scene.textures.exists('spark')) {
    const g = scene.add.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(C.UI_GOLD, 1);
    g.fillCircle(3, 3, 3);
    g.generateTexture('spark', 6, 6);
    g.destroy();
  }

  if (!scene.textures.exists('tile-grass')) {
    const tileSize = 32;
    const g = scene.add.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(C.BG, 1);
    g.fillRect(0, 0, tileSize, tileSize);
    g.lineStyle(1, C.BG_GRID, 1);
    g.strokeRect(0, 0, tileSize, tileSize);
    g.lineStyle(1, C.BG_GRID_LIGHT, 0.4);
    g.lineBetween(tileSize / 2, 0, tileSize / 2, tileSize);
    g.lineBetween(0, tileSize / 2, tileSize, tileSize / 2);
    g.fillStyle(C.BG_GRID_LIGHT, 0.5);
    g.fillCircle(4, 4, 1);
    g.fillCircle(tileSize - 4, tileSize - 4, 1);
    g.generateTexture('tile-grass', tileSize, tileSize);
    g.destroy();
  }
}

export const ENEMY_TEXTURES = {
  // Basic
  slime: 'enemy-slime',
  bat: 'enemy-bat',
  goblin: 'enemy-goblin',
  wolf: 'enemy-wolf',
  spider: 'enemy-spider',
  // Advanced
  skeleton: 'enemy-skeleton',
  darkKnight: 'enemy-darkKnight',
  cursedVillager: 'enemy-cursedVillager',
  slimeVariant: 'enemy-slimeVariant',
  // Expert
  redMage: 'enemy-redMage',
  apprentice: 'enemy-apprentice',
  royalSpider: 'enemy-royalSpider',
  fastSlime: 'enemy-fastSlime',
};

export const PICKUP_TEXTURES = {
  heart: 'pickup-heart',
  gem: 'pickup-gem',
  speed: 'pickup-speed',
  shield: 'pickup-shield',
  doubleDmg: 'pickup-doubleDmg',
};

// Real-sprite mapping for Kenney Tiny Dungeon. Used by PreloadScene.
export const KENNEY_SPRITES = {
  hero: 'assets/sprites/kenney/Tiles/tile_0084.png',
  // Basic
  'enemy-slime': 'assets/sprites/kenney/Tiles/tile_0108.png',
  'enemy-bat': 'assets/sprites/kenney/Tiles/tile_0120.png',
  'enemy-goblin': 'assets/sprites/kenney/Tiles/tile_0096.png',
  'enemy-wolf': 'assets/sprites/kenney/Tiles/tile_0122.png',
  'enemy-spider': 'assets/sprites/kenney/Tiles/tile_0085.png',
  // Advanced
  'enemy-skeleton': 'assets/sprites/kenney/Tiles/tile_0096.png',
  'enemy-darkKnight': 'assets/sprites/kenney/Tiles/tile_0097.png',
  'enemy-cursedVillager': 'assets/sprites/kenney/Tiles/tile_0085.png',
  'enemy-slimeVariant': 'assets/sprites/kenney/Tiles/tile_0108.png',
  // Expert
  'enemy-redMage': 'assets/sprites/kenney/Tiles/tile_0110.png',
  'enemy-apprentice': 'assets/sprites/kenney/Tiles/tile_0085.png',
  'enemy-royalSpider': 'assets/sprites/kenney/Tiles/tile_0122.png',
  'enemy-fastSlime': 'assets/sprites/kenney/Tiles/tile_0108.png',
  // Bosses
  boss: 'assets/sprites/kenney/Tiles/tile_0098.png',
  'boss-mistress': 'assets/sprites/kenney/Tiles/tile_0098.png',
  'boss-knight': 'assets/sprites/kenney/Tiles/tile_0086.png',
  'boss-captain': 'assets/sprites/kenney/Tiles/tile_0097.png',
};
